import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { useStore } from "../../../stores/store";
import { useSetMenu } from "../../../hooks/useSetMenu";
import useAuth from '../../../hooks/useAuth';
import { observer } from "mobx-react-lite";
import { useForm } from "react-hook-form";
import FormSelect from "../../Generic/FormSelect";
import { Card, Table, Badge, Modal, Button as BsButton } from 'react-bootstrap';
import { Button, CircularProgress } from '@mui/material';
import { PageHeader, StyledCard, EmptyState } from '../../Common/UI';
import { getThemeColor } from '../../../utils/applyTheme';
import './ExamRegistration.css';

const GradeManagement = observer(() => {
  const { t, i18n } = useTranslation();
  const { auth } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const axiosPrivate = useAxiosPrivate();
  const { mainStore } = useStore();

  // Set menu
  useSetMenu(mainStore.GradeManagementMenuType, mainStore.GradeManagementMenuNo);

  // Form hook
  const { register, setValue, formState: { errors } } = useForm();

  const [filters, setFilters] = useState({
    academicYearId: null,
    termRegistrationId: null,
    subjectId: null,
  });

  const [examRegistrations, setExamRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, registration: null });
  const [deletingId, setDeletingId] = useState(null);
  const [isWithinDateRange, setIsWithinDateRange] = useState(true); // Track if current date is within term registration dates

  // Dropdown options
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState(null);

  const [termRegistrations, setTermRegistrations] = useState([]);
  const [selectedTermRegistration, setSelectedTermRegistration] = useState(null);

  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);

  const universityId = auth?.universityId;

  // Load academic years - Only show years with graded exams
  useEffect(() => {
    const loadAcademicYears = async () => {
      try {
        const response = await axiosPrivate.get(`/StaffPortalExamRegistrations/GetAcademicYearsWithGradedExams/${universityId}`);
        setAcademicYears(response.data);

        if (selectedAcademicYear) {
          // Language change: update selected label only
          const updated = response.data.find(a => a.value === selectedAcademicYear.value);
          if (updated) setSelectedAcademicYear(updated);
        } else if (response.data && response.data.length > 0) {
          // Initial load: auto-select the latest (first) academic year
          const latestYear = response.data[0];
          setSelectedAcademicYear(latestYear);
          setValue('academicYearId', latestYear.value);
          setFilters(prev => ({ ...prev, academicYearId: latestYear.value }));
        }
      } catch (error) {
        console.error("Error loading academic years:", error);
      }
    };
    if (universityId) loadAcademicYears();
  }, [universityId, i18n.language]);

  // Load term registrations - Only show terms with graded exams
  useEffect(() => {
    const loadTermRegistrations = async () => {
      if (!filters.academicYearId) {
        setTermRegistrations([]);
        setSelectedTermRegistration(null);
        setValue('termRegistrationId', null);
        setIsWithinDateRange(true);
        return;
      }
      try {
        const response = await axiosPrivate.get(
          `/StaffPortalExamRegistrations/MyTermRegistrationsWithGradedExams?academicYearId=${filters.academicYearId}`
        );
        setTermRegistrations(response.data);

        if (selectedTermRegistration) {
          // Language change: update selected label only
          const updated = response.data.find(tr => tr.value === selectedTermRegistration.value);
          if (updated) setSelectedTermRegistration(updated);
        } else if (response.data && response.data.length > 0) {
          // Initial load: auto-select the latest (first) term registration
          const latestTerm = response.data[0];
          setSelectedTermRegistration(latestTerm);
          setValue('termRegistrationId', latestTerm.value);
          setFilters(prev => ({ ...prev, termRegistrationId: latestTerm.value }));

          // Validate date range
          validateDateRange(latestTerm);
        }
      } catch (error) {
        console.error("Error loading term registrations:", error);
      }
    };
    loadTermRegistrations();
  }, [filters.academicYearId, i18n.language]);

  // Load subjects - Only show subjects with graded exams
  useEffect(() => {
    const loadSubjects = async () => {
      if (!filters.termRegistrationId) {
        setSubjects([]);
        return;
      }
      try {
        const response = await axiosPrivate.get(
          `/StaffPortalExamRegistrations/MySubjectsWithGradedExams?termRegistrationId=${filters.termRegistrationId}`
        );
        setSubjects(response.data);
        // Update selected value label when language changes
        if (selectedSubject) {
          const updated = response.data.find(s => s.value === selectedSubject.value);
          if (updated) setSelectedSubject(updated);
        }
      } catch (error) {
        console.error("Error loading subjects:", error);
      }
    };
    loadSubjects();
  }, [filters.termRegistrationId, i18n.language]);

  // Load graded exam registrations
  const loadGradedRegistrations = async () => {
    if (!filters.termRegistrationId || !filters.subjectId) {
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.academicYearId) params.append('academicYearId', filters.academicYearId);
      if (filters.termRegistrationId) params.append('termRegistrationId', filters.termRegistrationId);
      if (filters.subjectId) params.append('subjectId', filters.subjectId);

      const response = await axiosPrivate.get(
        `/StaffPortalExamRegistrations/MyGradedRegistrations?${params.toString()}`
      );
      setExamRegistrations(response.data);
    } catch (error) {
      console.error("Error loading graded registrations:", error);
      enqueueSnackbar(t("errorLoadingData"), { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filters.termRegistrationId && filters.subjectId) {
      loadGradedRegistrations();
    }
  }, [filters.termRegistrationId, filters.subjectId]);

  // Validate if current date is within term registration date range
  const validateDateRange = (termRegistrationData) => {
    if (termRegistrationData && termRegistrationData.startDate && termRegistrationData.endDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

      const startDate = new Date(termRegistrationData.startDate);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(termRegistrationData.endDate);
      endDate.setHours(23, 59, 59, 999); // Set to end of day

      const withinRange = today >= startDate && today <= endDate;
      setIsWithinDateRange(withinRange);

      if (!withinRange) {
        console.log('Current date is outside term registration date range', {
          today: today.toISOString(),
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        });
      }
    } else {
      setIsWithinDateRange(true); // Default to true if dates not available
    }
  };

  // Handle select changes
  const handleSelectChange = (field, selectedOption) => {
    setValue(field, selectedOption?.value, { shouldValidate: true });

    switch (field) {
      case 'academicYearId':
        setSelectedAcademicYear(selectedOption);
        setFilters(prev => ({ ...prev, academicYearId: selectedOption ? selectedOption.value : null, termRegistrationId: null, subjectId: null }));
        setSelectedTermRegistration(null);
        setSelectedSubject(null);
        setValue('termRegistrationId', null);
        setValue('subjectId', null);
        break;
      case 'termRegistrationId':
        setSelectedTermRegistration(selectedOption);
        setFilters(prev => ({ ...prev, termRegistrationId: selectedOption ? selectedOption.value : null, subjectId: null }));
        setSelectedSubject(null);
        setValue('subjectId', null);
        break;
      case 'subjectId':
        setSelectedSubject(selectedOption);
        setFilters(prev => ({ ...prev, subjectId: selectedOption ? selectedOption.value : null }));
        break;
      default:
        break;
    }
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (registration) => {
    setDeleteDialog({ open: true, registration });
  };

  // Close delete dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, registration: null });
  };

  // Reset grade (delete to allow re-grading)
  const handleResetGrade = async () => {
    const registration = deleteDialog.registration;
    if (!registration) return;

    setDeletingId(registration.id);
    handleCloseDeleteDialog();

    try {
      await axiosPrivate.put(
        `/StaffPortalExamRegistrations/CancelGrade/${registration.id}`
      );

      enqueueSnackbar(t("gradeResetSuccessfully") || "Grade reset successfully. Student can now be re-graded.", { variant: "success" });

      // Remove from list
      setExamRegistrations(prev => prev.filter(r => r.id !== registration.id));
    } catch (error) {
      console.error('Error resetting grade:', error);
      enqueueSnackbar(t("errorResettingGrade") || "Error resetting grade", { variant: "error" });
    } finally {
      setDeletingId(null);
    }
  };

  // Format date to dd.MM.yyyy
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Get status badge class
  const getStatusBadgeClass = (statusId) => {
    switch (statusId) {
      case 1: return "status-registered"; // Rregullt
      case 2: return "status-pending";    // Transfer
      case 3: return "status-absent";     // Refuzuar
      case 4: return "status-pending";    // Abstenim
      case 5: return "status-absent";     // Jo Prezent
      default: return "status-pending";
    }
  };

  return (
    <div className="grade-management-container">
      <PageHeader
        icon="journal-check"
        title={t('resetGrades')}
        subtitle={t('manageAndResetStudentGrades')}
      />

      {/* Filter Panel */}
      <StyledCard className="mb-4 filter-card" hoverable={false}>
        <Card.Body>
          <h6 className="filter-title mb-3">
            <i className="bi bi-funnel-fill me-2"></i>
            {t('filterOptions')}
          </h6>
          <div className="row justify-content-start">
            <FormSelect
              label={t("academicYear")}
              name="academicYearId"
              register={register}
              errors={errors}
              options={academicYears}
              colSize={3}
              isClearable={false}
              required={false}
              disabled={true}
              value={selectedAcademicYear}
              onChange={(selectedOption) => handleSelectChange('academicYearId', selectedOption)}
            />
            <FormSelect
              label={t("termRegistration")}
              name="termRegistrationId"
              register={register}
              errors={errors}
              options={termRegistrations}
              colSize={3}
              isClearable={false}
              required={false}
              disabled={true}
              value={selectedTermRegistration}
              onChange={(selectedOption) => handleSelectChange('termRegistrationId', selectedOption)}
            />
            <FormSelect
              label={t("subject")}
              name="subjectId"
              register={register}
              errors={errors}
              options={subjects}
              colSize={3}
              isClearable={false}
              required={false}
              disabled={!filters.termRegistrationId}
              value={selectedSubject}
              onChange={(selectedOption) => handleSelectChange('subjectId', selectedOption)}
            />
            <div className="col-lg-3 mb-3">
              <div className="form-group">
                <label className="labelDescription">
                  <span className="labelText" style={{ visibility: 'hidden' }}>.</span>
                </label>
                <Button
                  variant="contained"
                  onClick={loadGradedRegistrations}
                  disabled={!filters.termRegistrationId || !filters.subjectId || loading}
                  fullWidth
                  sx={{
                    height: '38px',
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    backgroundColor: getThemeColor('primary', '#dc3545'),
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    boxShadow: `0 2px 5px ${getThemeColor('primary', '#dc3545')}40`,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: getThemeColor('hover', '#c82333'),
                      boxShadow: `0 4px 10px ${getThemeColor('primary', '#dc3545')}59`,
                      transform: 'translateY(-2px)',
                    },
                    '&:active': {
                      backgroundColor: getThemeColor('hover', '#c82333'),
                      transform: 'translateY(0)',
                      boxShadow: `0 2px 4px ${getThemeColor('primary', '#dc3545')}40`,
                    },
                    '&:disabled': {
                      backgroundColor: getThemeColor('primary', '#dc3545'),
                      color: '#fff',
                      opacity: 0.6,
                      transform: 'none',
                    },
                  }}
                >
                  {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t("show")}
                </Button>
              </div>
            </div>
          </div>
        </Card.Body>
      </StyledCard>

      {/* Results Section */}
      <StyledCard hoverable={false}>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="results-title mb-0">
              <i className="bi bi-people-fill me-2"></i>
              {t('gradedStudentsList')}
            </h6>
            <Badge bg="secondary" className="results-count">
              {examRegistrations.length} {t('gradedStudents')}
            </Badge>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <CircularProgress sx={{ color: 'var(--brand-red, #dc354d)' }} />
              <p className="mt-3 text-muted">{t('loadingGradedStudents')}</p>
            </div>
          ) : examRegistrations.length > 0 ? (
            <>
              {!isWithinDateRange && selectedTermRegistration && (
                <div className="alert alert-warning mb-3" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  <strong>{t("gradeResetNotAllowed")}</strong>
                  <br />
                  {t("gradeResetClosedMessage", {
                    endDate: selectedTermRegistration.endDate ? new Date(selectedTermRegistration.endDate).toLocaleDateString('en-GB') : ''
                  })}
                </div>
              )}
              <div className="table-responsive">
                <Table className="registered-grades-table" hover>
                <thead>
                  <tr>
                    <th>{t("studentId")}</th>
                    <th>{t("studentName")}</th>
                    <th>{t("subject")}</th>
                    <th className="text-center">{t("examDate")}</th>
                    <th className="text-center">{t("grade")}</th>
                    <th className="text-center">{t("gradeLetter")}</th>
                    <th className="text-center">{t("status")}</th>
                    <th className="text-center">{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {examRegistrations.map((registration) => (
                    <tr key={registration.id}>
                      <td>
                        <span className="student-id">{registration.idStudent}</span>
                      </td>
                      <td>
                        <div className="student-info">
                          <i className="bi bi-person-circle me-2 text-muted"></i>
                          <span>{registration.studentFirstName} {registration.studentLastName}</span>
                        </div>
                      </td>
                      <td>
                        <span className="subject-name">{registration.subjectName}</span>
                      </td>
                      <td className="text-center">
                        <span className="text-muted">{formatDate(registration.examDate)}</span>
                      </td>
                      <td className="text-center">
                        <span className={`grade-badge grade-${registration.gradeLetter || 'NA'}`}>
                          {registration.grade || 0}
                        </span>
                      </td>
                      <td className="text-center">
                        <strong>{registration.gradeLetter || '-'}</strong>
                      </td>
                      <td className="text-center">
                        {registration.grade === 0 && registration.gradeSystemDescription ? (
                          <span className="status-badge status-absent">
                            {registration.gradeSystemDescription}
                          </span>
                        ) : registration.examResultStatusTypeName ? (
                          <span className={`status-badge ${getStatusBadgeClass(registration.examResultStatusTypeId)}`}>
                            {registration.examResultStatusTypeName}
                          </span>
                        ) : (
                          <span className="status-badge status-pending">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        <BsButton
                          variant="outline-danger"
                          size="sm"
                          className="delete-btn"
                          onClick={() => handleOpenDeleteDialog(registration)}
                          disabled={deletingId === registration.id || !isWithinDateRange}
                        >
                          {deletingId === registration.id ? (
                            <CircularProgress
                              size={16}
                              sx={{ color: 'inherit' }}
                            />
                          ) : (
                            <>
                              <i className="bi bi-trash me-1"></i>
                              {t('reset')}
                            </>
                          )}
                        </BsButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              </div>
            </>
          ) : (
            filters.termRegistrationId && filters.subjectId && !loading && (
              <EmptyState
                icon="clipboard-check"
                title={t('noGradedStudentsFound')}
                message={t('noStudentsHaveBeenGradedYet')}
              />
            )
          )}
        </Card.Body>
      </StyledCard>

      {/* Delete Confirmation Modal */}
      <Modal show={deleteDialog.open} onHide={handleCloseDeleteDialog} centered>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {t("confirmResetGrade") || "Reset Grade?"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            {t("resetGradeConfirmation") || "Are you sure you want to reset this grade? The student will appear in the Exam Registration list again for re-grading."}
          </p>
          {deleteDialog.registration && (
            <div className="bg-light p-3 rounded">
              <p className="mb-2">
                <strong>{t("student")}:</strong>{' '}
                {deleteDialog.registration.studentFirstName} {deleteDialog.registration.studentLastName}
              </p>
              <p className="mb-0">
                <strong>{t("currentGrade")}:</strong>{' '}
                {deleteDialog.registration.grade} ({deleteDialog.registration.gradeLetter})
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <BsButton variant="secondary" onClick={handleCloseDeleteDialog}>
            <i className="bi bi-x-circle me-2"></i>
            {t("cancel")}
          </BsButton>
          <BsButton variant="danger" onClick={handleResetGrade}>
            <i className="bi bi-arrow-clockwise me-2"></i>
            {t("resetGrade") || "Reset Grade"}
          </BsButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default GradeManagement;
